'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface CompactAudioPlayerProps {
    audioSrc?: string;
    accentColor: string;
    className?: string;
    isEnabled?: boolean;
    isPlaying?: boolean;
    onPlayStateChange?: (playing: boolean) => void;
}

function formatTime(seconds: number): string {
    if (!isFinite(seconds) || isNaN(seconds)) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function CompactAudioPlayer({
    audioSrc,
    accentColor,
    className = '',
    isEnabled = true,
    isPlaying: externalIsPlaying,
    onPlayStateChange
}: CompactAudioPlayerProps) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const progressBarRef = useRef<HTMLDivElement>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);

    // Handle external play control (when another player starts)
    useEffect(() => {
        if (externalIsPlaying === false && isPlaying) {
            if (audioRef.current) {
                audioRef.current.pause();
            }
            setIsPlaying(false);
        }
    }, [externalIsPlaying, isPlaying]);

    // Reset state when audio source changes
    useEffect(() => {
        setCurrentTime(0);
        setDuration(0);
        setIsLoaded(false);
        setIsPlaying(false);
    }, [audioSrc]);

    // Audio event handlers - re-attach when audioSrc changes
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || !audioSrc) return;

        const handleTimeUpdate = () => {
            setCurrentTime(audio.currentTime);
        };

        const handleLoadedMetadata = () => {
            if (audio.duration && isFinite(audio.duration)) {
                setDuration(audio.duration);
                setIsLoaded(true);
                setIsLoading(false);
            }
        };

        const handleDurationChange = () => {
            if (audio.duration && isFinite(audio.duration)) {
                setDuration(audio.duration);
                setIsLoaded(true);
            }
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
            onPlayStateChange?.(false);
        };

        const handleCanPlay = () => {
            setIsLoading(false);
            if (audio.duration && isFinite(audio.duration)) {
                setDuration(audio.duration);
                setIsLoaded(true);
            }
        };

        const handleWaiting = () => {
            setIsLoading(true);
        };

        const handlePlaying = () => {
            setIsLoading(false);
        };

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('durationchange', handleDurationChange);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('canplay', handleCanPlay);
        audio.addEventListener('waiting', handleWaiting);
        audio.addEventListener('playing', handlePlaying);

        // Try to load metadata if not already loaded
        if (audio.readyState >= 1 && audio.duration && isFinite(audio.duration)) {
            setDuration(audio.duration);
            setIsLoaded(true);
        }

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('durationchange', handleDurationChange);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('waiting', handleWaiting);
            audio.removeEventListener('playing', handlePlaying);
        };
    }, [audioSrc, onPlayStateChange]);

    const togglePlay = useCallback(() => {
        if (!isEnabled || !audioSrc) return;

        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
            onPlayStateChange?.(false);
        } else {
            setIsLoading(true);
            audio.play()
                .then(() => {
                    setIsPlaying(true);
                    setIsLoading(false);
                    onPlayStateChange?.(true);
                })
                .catch((err) => {
                    console.error('Error playing audio:', err);
                    setIsLoading(false);
                });
        }
    }, [isPlaying, isEnabled, audioSrc, onPlayStateChange]);

    const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (!progressBarRef.current || !audioRef.current || !isEnabled || !isLoaded) return;

        const rect = progressBarRef.current.getBoundingClientRect();
        const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const newTime = percent * duration;

        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);
    }, [duration, isEnabled, isLoaded]);

    const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
    const hasAudio = !!audioSrc && isEnabled;

    return (
        <div className={cn('', className)}>
            {audioSrc && (
                <audio
                    ref={audioRef}
                    src={audioSrc}
                    preload="metadata"
                />
            )}

            <div className="flex items-center gap-3">
                {/* Play/Pause Button */}
                <button
                    onClick={togglePlay}
                    disabled={!hasAudio}
                    className={cn(
                        "w-9 h-9 rounded-full flex items-center justify-center transition-all flex-shrink-0",
                        hasAudio ? "hover:scale-105 cursor-pointer" : "opacity-40 cursor-not-allowed"
                    )}
                    style={{
                        background: hasAudio
                            ? `linear-gradient(to bottom right, ${accentColor}, ${accentColor}dd)`
                            : '#1f2937',
                    }}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                    {isLoading ? (
                        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="#0d1117" strokeWidth="3" strokeOpacity="0.3" />
                            <path d="M12 2a10 10 0 0 1 10 10" stroke="#0d1117" strokeWidth="3" strokeLinecap="round" />
                        </svg>
                    ) : isPlaying ? (
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-[#0d1117]">
                            <rect x="6" y="4" width="4" height="16" />
                            <rect x="14" y="4" width="4" height="16" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-[#0d1117] ml-0.5">
                            <polygon points="5,3 19,12 5,21" />
                        </svg>
                    )}
                </button>

                {/* Progress Bar */}
                <div className="flex-1 min-w-0">
                    <div
                        ref={progressBarRef}
                        onClick={hasAudio ? handleProgressClick : undefined}
                        className={cn(
                            "h-2 rounded-full overflow-hidden relative",
                            hasAudio ? "cursor-pointer group" : "cursor-not-allowed opacity-40"
                        )}
                        style={{
                            backgroundColor: `${accentColor}20`
                        }}
                    >
                        {/* Progress fill */}
                        <div
                            className="h-full rounded-full transition-all duration-150 ease-out"
                            style={{
                                width: `${progressPercent}%`,
                                background: `linear-gradient(to right, ${accentColor}, ${accentColor})`
                            }}
                        />
                        {/* Hover indicator */}
                        {hasAudio && (
                            <div
                                className="absolute top-0 right-0 bottom-0 left-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                style={{
                                    background: `linear-gradient(to right, transparent ${progressPercent}%, ${accentColor}30 ${progressPercent}%)`
                                }}
                            />
                        )}
                    </div>
                    <div className="flex justify-between text-[10px] font-mono text-[#6e7681] mt-1">
                        <span>{formatTime(currentTime)}</span>
                        <span>{isLoaded ? formatTime(duration) : (hasAudio ? 'Loading...' : '--:--')}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
